import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useImpactReportContext } from "@/context/ImpactReportContext";
import { useImpactReport, useImpactReportJob } from "@/hooks/useImpactReport";
import type { UseImpactReportPdfDownloadResult } from "@/interfaces/impactReportPdf";
import {
  buildImpactReportPageModel,
  isClientImpactReportDeferred,
} from "@/utils/impact-report-helper";
import {
  downloadImpactReportPdf,
  getImpactReportPdfDownloadSnapshot,
  subscribeImpactReportPdfDownload,
} from "@/utils/impact-report-pdf-download-helper";

/**
 * The one hook both PDF-download affordances mount — the control bar's
 * `ImpactReportDownloadControl` and the closing page's button. A download begun from either busies
 * both, because both read the same module-level snapshot.
 *
 * The report is read from the SAME queries `pages/ImpactReport.tsx` renders from: the same filter
 * and gate from context, so `useImpactReport` shares the page's cache key, and the same job id, so
 * a deferred report's finished job is the page's own cache entry. Neither issues a request the
 * page would not. `buildImpactReportPageModel` is pure, so running it over that identical payload
 * with the same `hasCauseSelection` reproduces the page array the hero view is drawing.
 *
 * `getImpactReportPdfDownloadSnapshot` doubles as the server snapshot: the PDF serialiser renders
 * the hero components with `renderToStaticMarkup`, which throws on a `useSyncExternalStore` that
 * has none.
 */
export function useImpactReportPdfDownload(): UseImpactReportPdfDownloadResult {
  const { filter, canRequestReport, hasCauseSelection } = useImpactReportContext();
  const { data: response, isError, isAwaitingUser } = useImpactReport(filter, canRequestReport);

  const deferral = isClientImpactReportDeferred(response) ? response : null;
  const { data: job, isError: isJobError } = useImpactReportJob(deferral ? deferral.jobId : null);
  const completedJobReport = job?.status === "completed" ? job.report : undefined;
  const report = isClientImpactReportDeferred(response) ? completedJobReport : response;

  const pages = useMemo(
    () => (report ? buildImpactReportPageModel(report, hasCauseSelection) : []),
    [report, hasCauseSelection],
  );

  const snapshot = useSyncExternalStore(
    subscribeImpactReportPdfDownload,
    getImpactReportPdfDownloadSnapshot,
    getImpactReportPdfDownloadSnapshot,
  );
  const pending = snapshot.status === "pending";

  // The page draws its error state, not the document, while either query is errored — even over
  // data a failed refetch left behind. An empty page model is an empty report, which the page
  // draws as an empty state: an enabled control there would export nothing.
  const canDownload =
    canRequestReport && !isAwaitingUser && !isError && !isJobError && pages.length > 0;

  const download = useCallback(() => {
    if (!canDownload || !report || pending) return;
    void downloadImpactReportPdf({
      pages,
      dateRange: report.data.dateRange,
      photoCredits: report.data.thankYou?.photoCredits,
      suppressCoverPage: hasCauseSelection,
      orgName: report.data.orgName,
    });
  }, [canDownload, report, pending, pages, hasCauseSelection]);

  return { pending, error: snapshot.errorMessage, canDownload, download };
}
